import React, { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Download, Trash2, Truck } from "lucide-react";
import { downloadCsv } from "@/lib/download";

export default function Dispatches() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/dispatches").then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const t = q.toLowerCase();
    return rows.filter((r) => (
      (r.invoice_no || "").toLowerCase().includes(t) ||
      (r.po_number || "").toLowerCase().includes(t) ||
      (r.customer_name || "").toLowerCase().includes(t) ||
      (r.material_no || "").toLowerCase().includes(t)
    ));
  }, [rows, q]);

  const totalValue = filtered.reduce((s, r) => s + (Number(r.dispatch_value) || 0), 0);

  const remove = async (id) => {
    if (!window.confirm("Delete this dispatch record? Pending qty on the PO line will be restored.")) return;
    try {
      await api.delete(`/dispatches/${id}`);
      toast.success("Deleted · Pending qty restored");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <Layout
      title="Dispatches"
      subtitle="All dispatched deliveries with invoice details — searchable & exportable to Excel"
      actions={
        <button onClick={() => downloadCsv("/export/dispatches", "dispatches.csv")} data-testid="export-dispatches-btn" className="text-xs text-slate-700 border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-50 transition-colors inline-flex items-center gap-2">
          <Download size={14} /> Export CSV / Excel
        </button>
      }
    >
      <div className="bg-white border border-gray-200 rounded-md">
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-gray-100 items-center">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              data-testid="dispatch-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search invoice #, PO #, customer, material..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-slate-300 focus:outline-none focus:border-slate-400"
            />
          </div>
          <div className="text-xs text-slate-700 font-mono whitespace-nowrap">
            {filtered.length} rows · Value ₹ {totalValue.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-sm text-gray-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center" data-testid="dispatches-empty">
            <Truck className="mx-auto text-gray-300 mb-3" size={32} />
            <div className="font-display text-lg font-semibold text-slate-900">No dispatches yet</div>
            <div className="text-sm text-gray-500 mt-1 max-w-md mx-auto">Every time you record a dispatch from a PO detail page (invoice + date), it will appear here.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="dispatches-table">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {["Dispatched On", "Invoice No", "Invoice Date", "PO No", "Customer", "SL#", "Material No", "Qty", "Rate", "Value", "Status", "Notes", ""].map((h, i) => (
                    <th key={`hdr-${i}`} className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-3 text-left whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <tr key={d.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`dispatch-row-${d.id}`}>
                    <td className="py-3 px-3 text-sm text-gray-600 font-mono">{(d.created_at || "").slice(0, 10)}</td>
                    <td className="py-3 px-3 text-sm font-mono text-slate-900 font-medium">{d.invoice_no || "—"}</td>
                    <td className="py-3 px-3 text-sm text-gray-600 font-mono">{d.invoice_date || "—"}</td>
                    <td className="py-3 px-3 text-sm">
                      <Link to={`/orders/${d.po_id}`} className="text-slate-900 hover:underline font-mono">{d.po_number || "—"}</Link>
                    </td>
                    <td className="py-3 px-3 text-sm text-slate-900">{d.customer_name || "—"}</td>
                    <td className="py-3 px-3 text-sm text-gray-600 font-mono text-right">{d.sr_no || "—"}</td>
                    <td className="py-3 px-3 text-sm text-gray-700 font-mono">{d.material_no || "—"}</td>
                    <td className="py-3 px-3 text-sm text-right text-slate-900 font-mono">{Number(d.dispatch_qty).toLocaleString("en-IN")}</td>
                    <td className="py-3 px-3 text-sm text-right text-gray-600 font-mono">{Number(d.unit_price).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                    <td className="py-3 px-3 text-sm text-right text-slate-900 font-mono font-semibold">{d.currency} {Number(d.dispatch_value).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                    <td className="py-3 px-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${d.line_closed_after ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                        {d.line_closed_after ? "Line closed" : "Partial"}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-600 max-w-[240px] truncate" title={d.notes || ""}>{d.notes || "—"}</td>
                    <td className="py-3 px-3 text-right">
                      <button onClick={() => remove(d.id)} data-testid={`delete-dispatch-${d.id}`} className="text-xs text-red-600 border border-red-200 rounded-md px-2 py-1 hover:bg-red-50 inline-flex items-center gap-1">
                        <Trash2 size={11} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
